import { MenuItem } from 'primeng/api';
import { Subscription } from 'rxjs';

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import { AppUserType } from '../../interfaces/credentials.interfaces';

export interface AppUserFormValues {
  clientId: string;
  clientSecret: string;
  clientCertificateFileName: File;
  clientCertificatePassword: string;
}

export interface AppUserFormGroup extends FormGroup {
  value: AppUserFormValues;
  controls: { [key in keyof AppUserFormValues]: AbstractControl };
}

interface ITabEvent {
  item: MenuItem;
}

@Component({
  selector: 'cds-app-user-form',
  templateUrl: './app-user-form.component.html',
  styleUrls: ['./app-user-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppUserFormComponent implements OnInit, OnDestroy {
  @Output() setFormValidStatus = new EventEmitter();
  @Output() selectedMenu = new EventEmitter<AppUserType>();
  activeMenu: MenuItem;
  @Input() secretMenuLabel: string =
    'CREDENTIALS.APPLICATION_USER.CLIENT_SECRET';
  @Input() certMenuLabel: string = 'CREDENTIALS.APPLICATION_USER.CERTIFICATE';
  @Input() appUserFormGroup: AppUserFormGroup;
  file: File;
  subs: Subscription[];
  get clientId() {
    return this.appUserFormGroup.value.clientId;
  }
  get clientSecret() {
    return this.appUserFormGroup.value.clientSecret;
  }
  get clientCertificateFileName(): string {
    if (
      this.appUserFormGroup.value.clientCertificateFileName !== undefined &&
      this.appUserFormGroup.value.clientCertificateFileName.name !== undefined
    ) {
      return this.appUserFormGroup.value.clientCertificateFileName.name;
    }
    return '';
  }
  get clientCertificatePassword() {
    return this.appUserFormGroup.value.clientCertificatePassword;
  }
  get clientIdAlias() {
    return this.appUserFormGroup.controls.clientId;
  }
  get clientSecretAlias() {
    return this.appUserFormGroup.controls.clientSecret;
  }
  get clientCertificateFileNameAlias() {
    return this.appUserFormGroup.controls.clientCertificateFileName;
  }

  menuItems: MenuItem[];
  AppUserType = AppUserType;
  constructor(private translate: TranslateService) {}
  selectedFile(file: File) {
    this.clientCertificateFileNameAlias.setValue(file);
  }

  ngOnDestroy() {
    this.subs.forEach((s) => s.unsubscribe());
  }

  ngOnInit() {
    this.subs = [];
    this.clientIdAlias.setValidators(Validators.required);
    this.clientIdAlias.updateValueAndValidity();
    this.menuItems = [
      this.translate.instant(this.secretMenuLabel),
      this.translate.instant(this.certMenuLabel),
    ].map((label) => this.createMenuItem(label));
    this.activeMenu = this.menuItems[AppUserType.Certificate];
    this.toggleAuthUserValidators();
  }
  private createMenuItem(label: string): MenuItem {
    return {
      label,
      command: (i) => this.toggleMenu(i),
    };
  }

  private toggleMenu(item: ITabEvent) {
    this.activeMenu = item.item;
    this.toggleAuthUserValidators();
  }

  private toggleAuthUserValidators() {
    if (this.activeMenu === this.menuItems[AppUserType.Certificate]) {
      this.clientCertificateFileNameAlias.setValidators([
        Validators.required,
        this.wrongFileFormat,
      ]);
      this.clientCertificateFileNameAlias.updateValueAndValidity({
        emitEvent: false,
      });
      this.clientSecretAlias.clearValidators();
      this.clientSecretAlias.updateValueAndValidity({ emitEvent: false });
      this.selectedMenu.emit(AppUserType.Certificate);
    } else {
      this.clientSecretAlias.setValidators(Validators.required);
      this.clientSecretAlias.updateValueAndValidity({ emitEvent: false });
      this.clientCertificateFileNameAlias.clearValidators();
      this.clientCertificateFileNameAlias.updateValueAndValidity({
        emitEvent: false,
      });
      this.selectedMenu.emit(AppUserType.Secret);
    }
    this.setFormValidStatus.emit();
  }
  private wrongFileFormat(c: FormControl): ValidationErrors | null {
    if (c.value === undefined || c.value === '') {
      return null;
    }
    const file: File = c.value;
    const endsWith = RegExp(/\.pem$/).test(file.name);

    return endsWith ? null : { wrongFormat: true };
  }
}
